import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  deleteDoc,
  doc
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { FaTrash, FaDownload } from 'react-icons/fa';

const CreditHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCreditHistory = async () => {
      if (!auth.currentUser) return;

      try {
        const interpretationQuery = query(
          collection(db, 'interpretations'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const imageGenerationQuery = query(
          collection(db, 'imageGenerations'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const [interpretationSnapshot, imageGenerationSnapshot] = await Promise.all([
          getDocs(interpretationQuery),
          getDocs(imageGenerationQuery)
        ]);

        const combinedHistory = [
          ...interpretationSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'Dream Interpretation'
          })),
          ...imageGenerationSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'Image Generation'
          }))
        ].sort((a, b) => b.createdAt - a.createdAt);

        setHistory(combinedHistory);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching credit history:', error);
        setError('Failed to load credit history');
        setLoading(false);
      }
    };

    fetchCreditHistory();
  }, []);

  const handleExportData = () => {
    const csvContent = [
      'Date,Type,Cost',
      ...history.map(item => 
        `${new Date(item.createdAt.seconds * 1000).toISOString()},${item.type},${item.cost}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'dreamarium_credit_history.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteItem = async (itemId, type) => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      const collectionName = type === 'Dream Interpretation' ? 'interpretations' : 'imageGenerations';
      await deleteDoc(doc(db, collectionName, itemId));
      
      // Remove item from local state
      setHistory(history.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  if (loading) return <div className="p-4 text-center">Loading credit history...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="credit-history p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Credit Usage History</h2>
        <button 
          onClick={handleExportData}
          className="flex items-center bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
        >
          <FaDownload className="mr-2" /> Export Data
        </button>
      </div>
      
      {history.length === 0 ? (
        <p className="text-gray-500">No credit usage history yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Date</th>
                <th className="border p-2 text-left">Type</th>
                <th className="border p-2 text-right">Credits Used</th>
                <th className="border p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="border p-2">
                    {new Date(item.createdAt.seconds * 1000).toLocaleString()}
                  </td>
                  <td className="border p-2">{item.type}</td>
                  <td className="border p-2 text-right">{item.cost.toFixed(2)} credits</td>
                  <td className="border p-2 text-center">
                    <button 
                      onClick={() => handleDeleteItem(item.id, item.type)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete this item"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CreditHistory;